import React, { memo } from 'react';
import { useSwipeable } from 'react-swipeable';
import type { State, Type } from './state';
import { useCarousel, TRIGGER } from './state';
import Element from '../../../core/Element';

export interface ItemsProps {
  children: React.ReactElement | React.ReactElement[];
  className?: string;
  customContainer?: string;
  onSwipe?: ((state: State, type: Type) => void) | undefined;
  $vertical?: boolean;
  setActiveIndex?: (ind: number) => void;
}

export const Items = memo(
  ({
    children,
    className,
    customContainer,
    onSwipe,
    $vertical,
    setActiveIndex,
  }: ItemsProps): React.ReactElement => {
    const [state, navigate] = useCarousel();
    React.useEffect(() => {
      if (setActiveIndex) {
        setActiveIndex(state.currentIndex);
      }
    }, [setActiveIndex, state, state.currentIndex]);

    const swipeHandlers = useSwipeable({
      onSwipedLeft: /* istanbul ignore next */ () =>
        navigate.next(onSwipe, TRIGGER.DOT),
      onSwipedRight: /* istanbul ignore next */ () =>
        navigate.previous(onSwipe, TRIGGER.DOT),
      preventScrollOnSwipe: true,
    });

    return (
      <div
        style={{ overflow: customContainer ? 'visible' : 'hidden' }}
        className={customContainer}
      >
        <Element
          aria-live="polite"
          aria-atomic="true"
          theme={{ visuallyHidden: true }}
        >{`Item ${state.currentIndex + 1} of ${state.totalItems}`}</Element>
        <ul
          className={className}
          style={{
            padding: 0,
            margin: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: $vertical ? 'column' : 'row',
            width: `${state.totalItems * 100}%`,
            transform: `translate${$vertical ? 'Y' : 'X'}(${
              0 - (state.currentIndex * 100) / state.totalItems
            }%)`,
            transition: `transform ${state.speed}ms ease-in-out`,
          }}
          {...swipeHandlers}
        >
          {children}
        </ul>
      </div>
    );
  }
);

export default Items;
